<template>
  <v-dialog :value="value" max-width="1250" scrollable @input="$emit('input', $event)" content-class="glass-dialog-wrapper">
    <v-card
      class="glass-card-theme elevation-24 d-flex flex-column overflow-hidden"
      style="height: 90vh; background: #121212 !important"
      :style="`border-top: 4px solid ${currentTheme.color}`">
      <div class="relative flex-shrink-0 header-transition" :style="headerStyle">
        <v-icon class="watermark-icon">{{ currentTheme.icon }}</v-icon>
        <div class="absolute-overlay" style="background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, #121212 100%)"></div>

        <div class="relative z-index-2 px-8 pt-8 pb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-sheet
              height="80"
              width="80"
              class="d-flex align-center justify-center rounded-xl elevation-10 mr-5"
              :style="`background: ${hexToRgba(currentTheme.color, 0.2)}; border: 1px solid ${currentTheme.color}; backdrop-filter: blur(10px)`">
              <v-icon size="40" :color="currentTheme.color">{{ currentTheme.icon }}</v-icon>
            </v-sheet>

            <div>
              <div class="text-overline font-weight-black letter-spacing-2 mb-0" :style="`color: ${currentTheme.color}`">
                {{ currentTheme.title.toUpperCase() }}
              </div>

              <div class="d-flex align-center mt-1" v-if="editData.customer">
                <v-avatar size="50" class="mr-3 elevation-5" style="border: 2px solid white">
                  <img :src="getPicture(customerDetails)" style="object-fit: cover" />
                </v-avatar>
                <div>
                  <h2 class="text-h4 font-weight-black white--text line-height-1 text-shadow">
                    {{ editData.customer.vorname }} {{ editData.customer.nachname }}
                  </h2>
                </div>
              </div>
              <h2 v-else class="text-h4 font-weight-black white--text mb-1 text-shadow mt-1">
                {{ localData.title || "Neuer Termin" }}
              </h2>

              <div class="d-flex align-center mt-2" v-if="editData.customer">
                <v-chip x-small color="rgba(255,255,255,0.15)" class="mr-2 white--text">
                  <v-icon x-small left color="grey lighten-1">mdi-cake</v-icon> {{ calculateAge(editData.customer.geburtstag) }}
                </v-chip>
                <v-chip v-if="isOnline" x-small color="#00E676" class="font-weight-bold black--text box-shadow-glow ml-2">
                  <v-icon x-small left color="black">mdi-circle</v-icon> Im Studio seit {{ currentCheckinDuration }}
                </v-chip>
                <v-chip
                  v-if="anamneseInfo.exists"
                  x-small
                  :color="anamneseInfo.isNew ? '#00963f' : '#FF9800'"
                  class="ml-2 white--text font-weight-bold">
                  <v-icon x-small left>mdi-clipboard-pulse</v-icon>
                  {{ anamneseInfo.isNew ? "Check:" : "Altbestand:" }} {{ formatDate(anamneseInfo.date) }}
                </v-chip>
              </div>
            </div>
          </div>

          <div class="d-flex align-center">
            <v-btn v-if="editData.customer" icon color="white" class="glass-btn mr-2" @click="showContactDialog = true" title="Kunden kontaktieren">
              <v-icon>mdi-message-badge-outline</v-icon>
            </v-btn>

            <v-btn v-if="editData.customer" text color="white" class="mr-2 opacity-70" @click="openPanel">
              <v-icon left small>mdi-view-dashboard-outline</v-icon> Panel öffnen
            </v-btn>
            <v-btn icon color="white" class="glass-btn" @click="$emit('input', false)">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="px-8 relative z-index-2 mt-4" v-if="editData.customer">
          <v-tabs v-model="activeTab" background-color="transparent" dark :slider-color="currentTheme.color">
            <v-tab class="font-weight-bold text-body-2"><v-icon left small>mdi-lightning-bolt</v-icon> Übersicht</v-tab>
            <v-tab class="font-weight-bold text-body-2"><v-icon left small>mdi-folder-open</v-icon> Akte & Pläne</v-tab>
            <v-tab class="font-weight-bold text-body-2" @click="loadHistory"><v-icon left small>mdi-history</v-icon> Historie</v-tab>
          </v-tabs>
        </div>
      </div>

      <v-card-text class="pt-6 pb-0 flex-grow-1 d-flex overflow-hidden">
        <v-row class="fill-height ma-0">
          <v-col cols="12" md="7" class="custom-scrollbar pr-6 pb-6" style="height: 100%; overflow-y: auto">
            <div v-if="loading && editData.customer" class="py-4">
              <v-skeleton-loader type="image, article" dark class="transparent"></v-skeleton-loader>
            </div>

            <div v-else-if="activeTab === 0 && editData.customer">
              <div class="hero-card mb-6 relative overflow-hidden rounded-xl elevation-10" :style="`border: 1px solid ${currentTheme.color}60`">
                <div class="relative z-index-2 pa-6">
                  <div class="d-flex justify-space-between mb-4">
                    <v-chip
                      small
                      :color="workflowState.done ? currentTheme.color : 'white'"
                      label
                      class="font-weight-black"
                      :class="workflowState.done ? 'white--text' : 'black--text'">
                      <v-icon left x-small>{{ workflowState.done ? "mdi-check" : "mdi-target" }}</v-icon>
                      {{ workflowState.stepText }}
                    </v-chip>
                  </div>

                  <div class="text-h5 font-weight-black white--text mb-1">{{ mainAction.text }}</div>
                  <div class="white--text opacity-80 mb-6" style="max-width: 80%">{{ mainAction.description }}</div>

                  <v-btn
                    block
                    x-large
                    color="white"
                    class="black--text font-weight-bold rounded-lg elevation-5"
                    @click="executeAction(mainAction.type)">
                    Jetzt Starten <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <div
                  class="absolute-full"
                  :style="`background: linear-gradient(135deg, ${hexToRgba(currentTheme.color, 0.4)} 0%, ${hexToRgba(
                    currentTheme.color,
                    0.1,
                  )} 100%);`"></div>
              </div>

              <div class="d-flex gap-3 mb-6">
                <div class="glass-panel-light flex-grow-1 pa-4 rounded-lg border-left-indicator" :style="`border-left-color: ${contractStatusColor}`">
                  <div class="caption grey--text text-uppercase">Vertrag</div>
                  <div class="font-weight-bold white--text text-body-1 text-truncate" style="max-width: 200px">
                    {{ customerDetails.mainContract ? customerDetails.mainContract.NAME : "Kein Vertrag" }}
                  </div>
                  <div class="caption" :style="`color: ${contractStatusColor}`">
                    {{ contractStatusText }}
                  </div>
                </div>

                <div
                  v-if="activeTimestop"
                  class="glass-panel-light flex-grow-1 pa-4 rounded-lg border-left-indicator"
                  style="border-left-color: #ff9800">
                  <div class="caption grey--text text-uppercase">Pausiert</div>
                  <div class="font-weight-bold white--text text-body-1">Bis {{ formatDate(activeTimestop.timestopInfos.end) }}</div>
                  <div class="caption orange--text">
                    {{ activeTimestop.tacData.Reason || "Pausiert" }}
                  </div>
                </div>
              </div>

              <div class="glass-panel-light rounded-lg pa-4 mb-4">
                <div class="subtitle-2 white--text mb-3 font-weight-bold">
                  <v-icon left small :color="currentTheme.color">mdi-door-open</v-icon> Letzte Besuche
                </div>
                <div v-if="processedAttendance.length > 0" class="d-flex align-center overflow-x-auto gap-2 pb-2 custom-scrollbar">
                  <div
                    v-for="(visit, i) in processedAttendance.slice(0, 5)"
                    :key="i"
                    class="checkin-pill d-flex align-center px-3 py-2 rounded-lg"
                    style="background: rgba(255, 255, 255, 0.08); min-width: 140px; border: 1px solid rgba(255, 255, 255, 0.1)">
                    <div class="mr-3 text-center line-height-1">
                      <div class="font-weight-black white--text">{{ $moment(visit.date).format("DD") }}</div>
                      <div class="caption grey--text" style="font-size: 0.6rem">{{ $moment(visit.date).format("MMM") }}</div>
                    </div>
                    <div>
                      <div class="caption white--text font-weight-bold">{{ visit.start }}</div>
                      <div class="caption green--text" style="font-size: 0.7rem">{{ visit.duration || "Aktiv" }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="caption grey--text">Keine Check-in Daten verfügbar.</div>
              </div>
            </div>

            <div v-else-if="activeTab === 1 && editData.customer" class="pb-4">
              <div class="mb-6">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="text-subtitle-1 font-weight-bold white--text">
                    <v-icon :color="currentTheme.color" class="mr-2">mdi-clipboard-pulse</v-icon> Anamnese & Gesundheit
                  </div>
                  <v-btn x-small :color="currentTheme.color" outlined @click="$refs.anamneseBogenRef.setVisible()">
                    <v-icon left x-small>mdi-plus</v-icon> Neu
                  </v-btn>
                </div>

                <div v-if="sortedMedicalHistory.length > 0">
                  <div
                    v-for="med in sortedMedicalHistory"
                    :key="med._id"
                    class="glass-panel-light pa-3 rounded-lg mb-2 d-flex align-center justify-space-between hover-glow cursor-pointer"
                    @click="viewDocument('medicalHistory', med)">
                    <div class="d-flex align-center">
                      <v-avatar color="green lighten-5" size="36" class="mr-3">
                        <v-icon small color="green darken-2">mdi-file-document-check</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-body-2 font-weight-bold white--text">Gesundheitscheck (V2)</div>
                        <div class="caption grey--text">{{ formatDate(med.createdAt) }} • {{ med.creator ? med.creator.vorname : "System" }}</div>
                      </div>
                    </div>
                    <v-icon small color="grey">mdi-eye</v-icon>
                  </div>
                </div>

                <div v-if="sortedOldAnamnese.length > 0" class="mt-2">
                  <div class="glass-panel-light pa-3 rounded-lg mb-2 d-flex align-center justify-space-between opacity-70">
                    <div class="d-flex align-center">
                      <v-avatar color="orange lighten-5" size="36" class="mr-3">
                        <v-icon small color="orange darken-3">mdi-archive-clock</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-body-2 font-weight-bold white--text">Anamnese (Altbestand)</div>
                        <div class="caption grey--text">
                          {{ formatDate(sortedOldAnamnese[0].createdAt || sortedOldAnamnese[0].date * 1000) }} • Archiviert
                        </div>
                      </div>
                    </div>
                    <v-chip x-small label dark>Nur Archiv</v-chip>
                  </div>
                </div>

                <div v-if="!anamneseInfo.exists" class="text-center py-4 glass-panel-light rounded-lg border-dashed">
                  <div class="caption grey--text">Keine Anamnese vorhanden</div>
                </div>
              </div>

              <v-divider class="my-4 border-light"></v-divider>

              <div class="mb-6">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="text-subtitle-1 font-weight-bold white--text">
                    <v-icon color="#1565C0" class="mr-2">mdi-dumbbell</v-icon> Trainingspläne
                  </div>
                  <v-btn x-small color="#1565C0" outlined @click="executeAction('workout')"> <v-icon left x-small>mdi-plus</v-icon> Neu </v-btn>
                </div>

                <div v-if="allWorkouts.length > 0">
                  <div
                    v-for="(plan, idx) in allWorkouts"
                    :key="idx"
                    class="glass-panel-light pa-3 rounded-lg mb-2 d-flex align-center justify-space-between hover-glow cursor-pointer"
                    :class="{ 'border-left-active': idx === 0 }"
                    @click="viewDocument('workout', plan)">
                    <div class="d-flex align-center overflow-hidden">
                      <v-avatar :color="plan.isV2 ? 'blue lighten-5' : 'grey lighten-3'" size="36" class="mr-3 flex-shrink-0">
                        <v-icon small :color="plan.isV2 ? 'blue darken-2' : 'grey darken-1'">
                          {{ plan.isV2 ? "mdi-cellphone-link" : "mdi-file-document-outline" }}
                        </v-icon>
                      </v-avatar>
                      <div class="overflow-hidden">
                        <div class="text-body-2 font-weight-bold white--text text-truncate">
                          {{ plan.title }} <v-chip v-if="idx === 0" x-small color="green" class="white--text ml-1" label>Aktuell</v-chip>
                        </div>
                        <div class="caption grey--text text-truncate">
                          {{ formatDate(plan.createdAt || plan.ts * 1000) }} • {{ plan.instructor }} • {{ plan.duration || "?" }} Min
                        </div>
                      </div>
                    </div>
                    <v-icon small color="grey">mdi-pencil</v-icon>
                  </div>
                </div>
                <div v-else class="text-center py-4 glass-panel-light rounded-lg border-dashed">
                  <div class="caption grey--text">Keine Pläne vorhanden</div>
                </div>
              </div>

              <v-divider class="my-4 border-light"></v-divider>

              <div class="mb-6">
                <div class="text-subtitle-1 font-weight-bold white--text mb-2">
                  <v-icon color="#FF9800" class="mr-2">mdi-file-sign</v-icon> Verträge & Pausen
                </div>

                <div class="glass-panel-light pa-3 rounded-lg mb-2" v-if="customerDetails.mainContract">
                  <div class="d-flex justify-space-between">
                    <span class="text-body-2 font-weight-bold white--text">{{ customerDetails.mainContract.NAME }}</span>
                    <v-chip x-small :color="contractStatusColor" outlined class="font-weight-bold">{{
                      customerDetails.mainContract.CONTRACTSTATE
                    }}</v-chip>
                  </div>
                  <div class="d-flex justify-space-between mt-2 caption grey--text">
                    <span>Start: {{ formatDate(customerDetails.mainContract.STARTDATE) }}</span>
                    <span>Ende: {{ formatDate(customerDetails.mainContract.TERM_ENDDATE) }}</span>
                  </div>
                </div>

                <div v-if="sortedTimestops.length > 0">
                  <div class="caption font-weight-bold grey--text mb-1 mt-3">Timestop Historie</div>
                  <div v-for="ts in sortedTimestops" :key="ts._id" class="d-flex align-center justify-space-between py-1 border-bottom-light">
                    <div>
                      <div class="caption white--text">{{ formatDate(ts.timestopInfos.start) }} - {{ formatDate(ts.timestopInfos.end) }}</div>
                      <div class="caption grey--text" style="font-size: 10px">{{ ts.tacData.Reason }}</div>
                    </div>
                    <v-icon v-if="ts.status === 'accepted'" small color="green">mdi-check-circle</v-icon>
                    <v-icon v-else-if="ts.status === 'rejected'" small color="red">mdi-close-circle</v-icon>
                    <v-icon v-else small color="grey">mdi-help-circle</v-icon>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="activeTab === 2 && editData.customer" class="pl-2 pr-2">
              <div v-if="loadingHistory" class="text-center py-4">
                <v-progress-circular indeterminate :color="currentTheme.color"></v-progress-circular>
              </div>
              <div v-else-if="appointmentHistory.length > 0">
                <div v-for="appt in appointmentHistory" :key="appt._id" class="mb-4 relative pl-6 timeline-item">
                  <div class="timeline-line"></div>
                  <div class="timeline-dot" :style="`background: ${appt.color || currentTheme.color}`"></div>
                  <div class="glass-panel-light pa-3 rounded-lg hover-glow cursor-pointer" @click="openHistoryDetails(appt)">
                    <div class="d-flex justify-space-between">
                      <span class="font-weight-bold white--text">{{ appt.title || "Termin" }}</span>
                      <span class="caption grey--text">{{ formatDate(appt.date) }}</span>
                    </div>
                    <div class="d-flex align-center mt-2">
                      <v-avatar size="20" class="mr-2">
                        <img v-if="appt.assignedEmployee && appt.assignedEmployee.bild" :src="'/img/profilbilder/' + appt.assignedEmployee.bild" />
                        <v-icon v-else x-small>mdi-account</v-icon>
                      </v-avatar>
                      <span class="caption grey--text">{{ appt.assignedEmployee ? appt.assignedEmployee.vorname : "Unbekannt" }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center caption grey--text py-4">Keine vergangenen Termine.</div>
            </div>

            <div v-else-if="!editData.customer" class="fill-height d-flex align-center justify-center flex-column opacity-30">
              <v-icon size="100" color="grey">mdi-account-search</v-icon>
              <div class="text-h6 mt-4 white--text">Wähle einen Kunden aus</div>
            </div>
          </v-col>

          <v-col cols="12" md="5" class="pl-6 border-left-light d-flex flex-column" style="background: rgba(0, 0, 0, 0.2)">
            <div class="d-flex align-center mb-6 mt-2">
              <v-icon :color="currentTheme.color" class="mr-3">mdi-clipboard-text-outline</v-icon>
              <div class="text-h6 font-weight-bold white--text">Termin Details</div>
            </div>

            <div class="flex-grow-1 overflow-y-auto custom-scrollbar pr-2">
              <div class="d-flex gap-2 mb-2">
                <v-menu offset-y :close-on-content-click="false" transition="scale-transition">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="localData.date"
                      label="Datum"
                      outlined
                      dense
                      dark
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      prepend-inner-icon="mdi-calendar"
                      class="rounded-lg"
                      background-color="rgba(255,255,255,0.05)"
                      :color="currentTheme.color"></v-text-field>
                  </template>
                  <v-date-picker v-model="localData.date" no-title scrollable :color="currentTheme.color"></v-date-picker>
                </v-menu>

                <v-select
                  v-model="localData.colId"
                  :items="employees"
                  item-text="name"
                  item-value="id"
                  label="Mitarbeiter"
                  outlined
                  dense
                  dark
                  hide-details
                  prepend-inner-icon="mdi-account"
                  class="rounded-lg"
                  background-color="rgba(255,255,255,0.05)"
                  :color="currentTheme.color"
                  :item-color="currentTheme.color"></v-select>
              </div>

              <div class="d-flex gap-2 mb-4">
                <v-text-field
                  v-model="localData.start"
                  label="Start"
                  type="time"
                  outlined
                  dense
                  dark
                  hide-details
                  class="rounded-lg centered-input"
                  background-color="rgba(255,255,255,0.05)"
                  :color="currentTheme.color"></v-text-field>
                <v-text-field
                  v-model="localData.duration"
                  label="Minuten"
                  type="number"
                  outlined
                  dense
                  dark
                  hide-details
                  class="rounded-lg centered-input"
                  background-color="rgba(255,255,255,0.05)"
                  :color="currentTheme.color"></v-text-field>
              </div>

              <div class="mb-4">
                <v-text-field
                  v-model="localData.title"
                  label="Titel"
                  outlined
                  dense
                  dark
                  class="rounded-lg font-weight-bold"
                  background-color="rgba(255,255,255,0.05)"
                  :color="currentTheme.color"></v-text-field>
              </div>

              <div class="mb-4">
                <v-select
                  v-model="localData.status"
                  :items="statusOptions"
                  label="Status"
                  outlined
                  dense
                  dark
                  class="rounded-lg"
                  item-text="text"
                  item-value="value"
                  :prepend-inner-icon="statusIcon"
                  background-color="rgba(255,255,255,0.05)"
                  :color="currentTheme.color"
                  :item-color="currentTheme.color">
                </v-select>
              </div>

              <div
                class="notepad-wrapper d-flex flex-column rounded-lg overflow-hidden flex-grow-1 mb-4"
                :style="`border: 1px solid ${currentTheme.color}30`">
                <div class="pa-2 d-flex align-center" :style="`background: ${hexToRgba(currentTheme.color, 0.2)}`">
                  <span class="caption font-weight-bold text-uppercase" :style="`color: ${currentTheme.color}`">Notizen & Ablauf</span>
                </div>
                <v-textarea
                  v-model="localData.description"
                  placeholder="Ergebnisse, Gewichte oder Besonderheiten..."
                  solo
                  flat
                  background-color="transparent"
                  dark
                  class="flex-grow-1 pt-2 custom-textarea white--text"
                  hide-details
                  :color="currentTheme.color"></v-textarea>
              </div>

              <div class="d-flex justify-space-between align-center px-2">
                <span class="caption grey--text">Label Farbe</span>
                <div class="d-flex gap-1">
                  <div
                    v-for="c in colorOptions"
                    :key="c"
                    @click="localData.color = c"
                    class="color-dot"
                    :class="{ active: localData.color === c }"
                    :style="`background: ${c}; border-color: ${localData.color === c ? 'white' : 'transparent'}`"></div>
                </div>
              </div>
            </div>

            <div class="py-4 mt-auto border-top-light d-flex justify-space-between align-center">
              <v-btn small text color="red darken-1" @click="$emit('delete', localData)"><v-icon left small>mdi-trash-can</v-icon> Löschen</v-btn>
              <v-btn large depressed :color="currentTheme.color" class="white--text font-weight-bold px-8 rounded-lg box-shadow-glow" @click="save">
                Speichern
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <anamnese-bogen ref="anamneseBogenRef"></anamnese-bogen>
      <upgrade-contract
        v-if="editData.customer && showContractDialog"
        :selectedCustomer="editData.customer._id"
        @hide="showContractDialog = false"
        :dialog="showContractDialog"
        @created="$toast.success('Vertrag angepasst')"></upgrade-contract>

      <v-dialog v-model="showHistoryDialog" max-width="500" content-class="glass-dialog-wrapper">
        <v-card class="glass-card-theme pa-5" style="background: #1e1e1e !important; border: 1px solid #333" v-if="selectedHistoryEntry">
          <div class="d-flex justify-space-between align-start">
            <div class="text-h5 white--text font-weight-bold mb-1">{{ selectedHistoryEntry.title || "Termin" }}</div>
            <v-btn icon small class="white--text" @click="showHistoryDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <div class="caption grey--text mb-4">
            {{ formatDate(selectedHistoryEntry.date) }} • {{ selectedHistoryEntry.assignedEmployee?.vorname }}
          </div>
          <div
            class="glass-panel-light pa-3 rounded-lg white--text text-body-2 custom-scrollbar"
            style="white-space: pre-wrap; max-height: 400px; overflow-y: auto">
            {{ selectedHistoryEntry.description || "Keine Notizen" }}
          </div>
        </v-card>
      </v-dialog>
    </v-card>
    <user-contact-dialog v-model="showContactDialog" :user="editData.customer" :color="currentTheme.color"> </user-contact-dialog>
  </v-dialog>
</template>

<script>
import moment from "moment"
import { mapActions } from "vuex"
import AnamneseBogen from "@/components/anamneseBogenCreate"
import UpgradeContract from "@/views/dashboard/contract/components/upgradeContract_sebredesign"
import UserContactDialog from "@/components/tools/contactDialog" // Pfad anpassen falls nötig
import api from "@/api"

export default {
  name: "WorkCalendarEditDialog",
  components: { AnamneseBogen, UpgradeContract, UserContactDialog },
  props: ["value", "editData", "customerDetails", "loading", "employees", "currentDate"],
  data() {
    return {
      activeTab: 0,
      localData: {},
      colorOptions: ["#2196F3", "#4CAF50", "#FF9800", "#F44336", "#9C27B0", "#00BCD4", "#FFD700"],
      statusOptions: [
        { text: "Geplant", value: "planned" },
        { text: "Erschienen (Check-in)", value: "checkedin" },
        { text: "Abgesagt", value: "cancelled" },
        { text: "No-Show (Nicht erschienen)", value: "noshow" },
      ],
      appointmentHistory: [],
      loadingHistory: false,
      showHistoryDialog: false,
      selectedHistoryEntry: null,
      showContractDialog: false,
      showContactDialog: false,
    }
  },
  watch: {
    editData: {
      immediate: true,
      handler(val) {
        if (val) {
          this.localData = JSON.parse(JSON.stringify(val))
          if (!this.localData.date) {
            this.$set(this.localData, "date", this.currentDate)
          }
          if (!this.localData.status) this.$set(this.localData, "status", "planned")
          this.activeTab = 0
          this.appointmentHistory = []
        }
      },
    },
  },
  computed: {
    statusIcon() {
      const s = this.localData.status
      if (s === "checkedin") return "mdi-check-circle"
      if (s === "cancelled") return "mdi-cancel"
      if (s === "noshow") return "mdi-eye-off"
      return "mdi-calendar-clock"
    },
    // ---- THEME LOGIC ----
    currentTheme() {
      // 1. Priorität: Termin-Farbe
      if (this.localData.color) {
        return {
          color: this.localData.color,
          icon: this.localData.icon || this.getIconByType(),
          title: this.localData.title || "Termin",
        }
      }

      // 2. Fallback
      const type = (this.editData.specialType || this.localData.specialType || "").toLowerCase()

      if (type === "trial") return { color: "#FF9800", icon: "mdi-account-star", title: "Probetraining" }
      if (type === "intro") return { color: "#1565C0", icon: "mdi-rocket-launch", title: "Einführung" }
      if (type === "pt") return { color: "#FBC02D", icon: "mdi-crown", title: "Personal Training" }
      if (type === "checkup") return { color: "#D32F2F", icon: "mdi-heart-pulse", title: "Gesundheits-Check" }
      if (type === "upgrade") return { color: "#00963f", icon: "mdi-star-circle", title: "Upgrade / Vertrag" }

      return { color: "#00963f", icon: "mdi-dumbbell", title: "Workout / Termin" }
    },
    headerStyle() {
      const c = this.currentTheme.color
      return `background: radial-gradient(circle at top right, ${this.hexToRgba(c, 0.4)} 0%, transparent 70%);`
    },

    // ---- DATEN LOGIK ----
    anamneseInfo() {
      const mh = this.customerDetails?.documents?.medicalHistory || []
      const old = this.customerDetails?.documents?.anamnese || []

      if (mh.length > 0) {
        const newest = [...mh].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
        return { exists: true, isNew: true, date: newest.createdAt, obj: newest }
      }
      if (old.length > 0) {
        const newest = [...old].sort((a, b) => b.date - a.date)[0]
        const d = newest.date > 10000000000 ? newest.date : newest.date * 1000
        return { exists: true, isNew: false, date: d, obj: newest }
      }
      return { exists: false }
    },
    sortedMedicalHistory() {
      return (this.customerDetails?.documents?.medicalHistory || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },
    sortedOldAnamnese() {
      return (this.customerDetails?.documents?.anamnese || []).sort((a, b) => b.date - a.date)
    },
    allWorkouts() {
      const v2 = (this.customerDetails?.workoutsV2 || []).map((w) => ({ ...w, isV2: true, dateObj: new Date(w.createdAt || w.ts * 1000) }))
      const v1 = (this.customerDetails?.workouts || []).map((w) => ({ ...w, isV2: false, dateObj: new Date(w.ts * 1000) }))
      return [...v2, ...v1].sort((a, b) => b.dateObj - a.dateObj)
    },
    sortedTimestops() {
      return (this.customerDetails?.documents?.timestops || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },
    workflowState() {
      const type = (this.currentTheme.title || "").toLowerCase()
      const needsAnamnese = ["probetraining", "einführung", "personal training"].some((t) => type.includes(t))
      if (needsAnamnese && !this.anamneseInfo.exists) return { done: false, stepText: "Anamnese notwendig" }
      return { done: true, stepText: "Alles bereit" }
    },
    mainAction() {
      if (!this.workflowState.done)
        return { type: "anamnese", text: "Anamnese durchführen", description: "Es liegen keine Gesundheitsdaten vor.", icon: "mdi-clipboard-alert" }

      const hasPlan = this.allWorkouts.length > 0
      const title = (this.currentTheme.title || "").toLowerCase()
      if (title.includes("vertrag"))
        return { type: "upgrade", text: "Vertrag anpassen", description: "Upgrade oder Laufzeitänderung.", icon: "mdi-star-shooting" }
      if (!hasPlan)
        return { type: "workout", text: "Ersten Plan erstellen", description: "Kunde hat noch keinen Trainingsplan.", icon: "mdi-dumbbell" }
      return { type: "workout", text: "Trainingsplan bearbeiten", description: "Aktuellen Plan öffnen.", icon: "mdi-file-document-edit" }
    },
    contractStatusColor() {
      const state = this.customerDetails?.mainContract?.CONTRACTSTATE
      if (state === "Laufend") return "#00E676"
      if (state === "Gekündigt") return "#FF5252"
      return "grey"
    },
    contractStatusText() {
      const c = this.customerDetails?.mainContract
      if (!c) return ""
      if (c.CONTRACTSTATE === "Laufend") return `Läuft bis ${this.formatDate(c.TERM_ENDDATE)}`
      return c.CONTRACTSTATE
    },
    activeTimestop() {
      if (!this.customerDetails?.documents?.timestops) return null
      return this.customerDetails.documents.timestops.find(
        (t) => t.status === "accepted" && moment().isBetween(t.timestopInfos.start, t.timestopInfos.end, "day", "[]"),
      )
    },
    isOnline() {
      return this.customerDetails && this.customerDetails.checkinDate && !this.customerDetails.checkoutDate
    },
    currentCheckinDuration() {
      if (!this.isOnline) return ""
      const diff = moment().diff(moment(this.customerDetails.checkinDate), "minutes")
      return `${Math.floor(diff / 60)}h ${diff % 60}m`
    },
    processedAttendance() {
      if (!this.customerDetails?.attendance) return []
      let rawEvents = []
      if (typeof this.customerDetails.attendance === "object" && !Array.isArray(this.customerDetails.attendance)) {
        Object.values(this.customerDetails.attendance).forEach((dayEvents) => rawEvents.push(...dayEvents))
      } else if (Array.isArray(this.customerDetails.attendance)) {
        rawEvents = this.customerDetails.attendance
      }
      rawEvents.sort((a, b) => new Date(a.date + " " + a.time) - new Date(b.date + " " + b.time))
      const visits = []
      let currentVisit = null
      rawEvents.forEach((entry) => {
        if (entry.type === "checkin") {
          currentVisit = {
            date: entry.date,
            start: moment(entry.time, "HH:mm:ss").format("HH:mm"),
            end: null,
            duration: null,
            fullStartDate: moment(entry.date + " " + entry.time),
          }
          visits.push(currentVisit)
        } else if (entry.type === "checkout" && currentVisit && !currentVisit.end) {
          currentVisit.end = moment(entry.time, "HH:mm:ss").format("HH:mm")
          const diff = moment(entry.date + " " + entry.time).diff(currentVisit.fullStartDate, "minutes")
          currentVisit.duration = `${Math.floor(diff / 60)}Std ${diff % 60}Min`
          currentVisit = null
        }
      })
      return visits.reverse()
    },
  },
  methods: {
    ...mapActions("panelStore", ["selectCustomer"]),
    getIconByType() {
      return "mdi-calendar"
    },
    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(0,150,63, ${alpha})`
      if (hex.startsWith("#")) {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
      return hex
    },
    getPicture(customer) {
      if (!customer) return "/img/icons/user_placeholder.png"
      if (customer.bild?.includes("uploads")) return "/img/profilbilder/" + customer.bild
      if (customer.bild) return "data:image/jpeg;base64," + customer.bild
      return "/img/icons/user_placeholder.png"
    },
    viewDocument(type, item) {
      if (type === "workout") {
        this.$router.push(`/workouts/createnew?userId=${this.editData.customer._id}`)
      } else if (type === "medicalHistory") {
        this.$refs.anamneseBogenRef.setVisible(item)
      }
    },
    async loadHistory() {
      if (!this.editData.customer?._id || this.appointmentHistory.length > 0) return
      this.loadingHistory = true
      try {
        const response = await api.shiftManagement.fetchHistory(this.editData.customer._id)
        this.appointmentHistory = response.data
      } catch (e) {
        this.$toast.error("Historie Fehler")
      } finally {
        this.loadingHistory = false
      }
    },
    openHistoryDetails(appt) {
      this.selectedHistoryEntry = appt
      this.showHistoryDialog = true
    },
    executeAction(type) {
      this.selectCustomer(this.editData.customer._id)
      if (type === "workout") this.$router.push(`/workouts/createnew?userId=${this.editData.customer._id}`)
      else if (type === "anamnese") this.$refs.anamneseBogenRef.setVisible()
      else if (type === "upgrade") this.showContractDialog = true
    },
    openPanel() {
      this.selectCustomer(this.editData.customer._id)
      this.$router.push("/panel")
    },
    save() {
      this.$emit("save", this.localData)
    },
    formatDate(d) {
      if (!d) return ""
      const date = typeof d === "number" && d < 2000000000 ? new Date(d * 1000) : new Date(d)
      return moment(date).format("DD.MM.YY")
    },
    calculateAge(d) {
      return d ? moment().diff(moment(d), "years") : ""
    },
  },
}
</script>

<style scoped>
/* Glass UI */
.glass-dialog-wrapper {
  box-shadow: none;
}
.glass-panel-light {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.2s;
}
.border-light {
  border-color: rgba(255, 255, 255, 0.06) !important;
}
.absolute-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}
.watermark-icon {
  position: absolute;
  right: -20px;
  top: -30px;
  font-size: 280px !important;
  opacity: 0.03;
  transform: rotate(-10deg);
  pointer-events: none;
}
.hover-glow {
  transition: all 0.15s ease;
}
.hover-glow:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.12);
  transform: translateX(2px);
}
.border-left-indicator {
  border-left: 3px solid;
}
.border-left-active {
  border-left: 3px solid #4caf50;
  background: linear-gradient(90deg, rgba(76, 175, 80, 0.08), transparent);
}
.border-dashed {
  border: 1px dashed rgba(255, 255, 255, 0.06) !important;
}
.border-bottom-light {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.border-left-light {
  border-left: 1px solid rgba(255, 255, 255, 0.06) !important;
}
.border-top-light {
  border-top: 1px solid rgba(255, 255, 255, 0.06) !important;
}

/* Glass Button */
.glass-btn {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 8px !important;
}

/* Box Shadow Glow */
.box-shadow-glow {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

/* Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* Timeline */
.timeline-item {
  position: relative;
}
.timeline-line {
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.06);
}
.timeline-dot {
  position: absolute;
  left: 6px;
  top: 20px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-shadow: 0 0 8px currentColor;
}

/* Forms */
.centered-input >>> input {
  text-align: center;
  font-weight: 700;
}
.color-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.15s;
  opacity: 0.4;
  border: 2px solid transparent;
}
.color-dot:hover {
  opacity: 0.7;
  transform: scale(1.1);
}
.color-dot.active {
  transform: scale(1.25);
  opacity: 1;
  border-color: white;
  box-shadow: 0 0 8px currentColor;
}

/* Custom Textarea */
.custom-textarea >>> .v-input__slot {
  min-height: 80px !important;
}
.custom-textarea >>> textarea {
  font-size: 0.85rem;
}

/* Notepad */
.notepad-wrapper {
  background: rgba(0, 0, 0, 0.2);
}

/* Typography */
.text-shadow {
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}
.line-height-1 {
  line-height: 1;
}
.letter-spacing-2 {
  letter-spacing: 2px;
}
.opacity-70 {
  opacity: 0.7;
}
.opacity-80 {
  opacity: 0.8;
}

/* Gaps */
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}

/* Checkin Pill */
.checkin-pill {
  transition: all 0.15s;
}
.checkin-pill:hover {
  background: rgba(255, 255, 255, 0.12) !important;
}

/* Z-Index */
.z-index-2 {
  z-index: 2;
}
.z-index-20 {
  z-index: 20;
}
</style>
